import { FC } from "react";
import { useIntl } from "react-intl";

import { Box, Typography } from "@mui/material";

import { useUser } from "@framework/provider-user";
import type { IUser } from "@framework/types";

import { formatMeasurementNumber } from "../../../scheme/utils/measurement-format";
import { formatCascadeUnitLabel, type ICascadeDataMark } from "./model";
import { CASCADE_TOOLTIP_COLORS, getCascadeTooltipLines } from "./tooltip-utils";

export const CascadeDataMarkTooltip: FC<{ mark: ICascadeDataMark }> = ({ mark }) => {
  const { formatMessage } = useIntl();
  const { profile } = useUser<IUser>();
  const formatValue = (value: number, unitCode: number | null) =>
    formatMeasurementNumber(value, unitCode, profile.interfaceSettings.dataFormat, { fallbackDigits: 2 });
  const control = mark.cascade.controlParameter;
  const lines = getCascadeTooltipLines(
    {
      pointName: mark.pointName,
      measureName: mark.measureName,
      date: mark.date,
      value: mark.value,
      unitCode: mark.units,
      units: formatCascadeUnitLabel(formatMessage({ id: `enums.units.${mark.units}` })),
    },
    {
      point: formatMessage({ id: "pages.multiAnalysis.timePanel.tooltip.point" }),
      measure: formatMessage({ id: "pages.multiAnalysis.timePanel.tooltip.measure" }),
      dateTime: formatMessage({ id: "pages.multiAnalysis.timePanel.tooltip.dateTime" }),
      value: formatMessage({ id: "pages.multiAnalysis.timePanel.tooltip.value" }),
    },
    formatValue,
  );

  return (
    <Box
      sx={{
        p: 1,
        maxWidth: 360,
        bgcolor: CASCADE_TOOLTIP_COLORS.background,
        color: CASCADE_TOOLTIP_COLORS.body,
        borderRadius: 1,
        overflowWrap: "anywhere",
        ".MuiTooltip-tooltip &": { m: "-4px -8px" },
      }}
    >
      {lines.map((line, index) => (
        <Typography
          key={index}
          sx={{
            fontSize: 12,
            fontWeight: index === 0 ? 600 : 400,
            color: index === 0 ? CASCADE_TOOLTIP_COLORS.title : "inherit",
          }}
        >
          {line}
        </Typography>
      ))}
      {control ? (
        <Typography sx={{ fontSize: 12, color: "inherit" }}>
          {control.title}: {formatValue(control.value, null)} {control.units}
        </Typography>
      ) : null}
    </Box>
  );
};
